import group from '@tk/utilities/tk.array.group';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';

interface ArtSpecRow {
    groupId: number;
    groupName: string;
    label: string;
    value: string;
}

interface ArtSpecData {
    data: ArtSpecRow[];
}

export default class TKArtSpecTable extends TKCustomElementFactory {
    url?: string;
    groupTemplateElement?: HTMLTemplateElement;
    rowTemplateElement?: HTMLTemplateElement;
    listWrapper?: HTMLElement;
    loadedClassName: string;
    colorClassName: string;
    colorHexRegex: RegExp;

    constructor() {
        super();

        this.url = this.getAttribute('data-tk-url') || undefined;
        this.groupTemplateElement = this.querySelector('[data-tk-template-list-group]') || undefined;
        this.rowTemplateElement = this.querySelector('[data-tk-template-list-row]') || undefined;
        this.listWrapper = this.querySelector('[data-tk-list-wrapper]') || undefined;
        this.loadedClassName = this.getAttribute('[data-tk-loaded-class-name]') || 'tk-list-description--loaded';
        this.colorClassName = this.getAttribute('[data-tk-color-class-name]') || 'tk-list-description__color';
        this.colorHexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    }

    connectedCallback(): void {
        if (
            !this.url
            || !this.groupTemplateElement
            || !this.rowTemplateElement
            || !this.listWrapper
        ) throw new Error('ArtSpecTable: Elements or URL are missing!');
        this.fetch();
    }

    fetch() {
        fetchRequest({
            requestURL: this.url!,
            resolveHandler: this.refreshContent.bind(this),
        });
    }

    refreshContent(response: TKResponse<ArtSpecData>) {
        if (!response || !response.success) return;
        const { data } = response.dataAsJson;
        const groupedList = Object.values(group(data, 'groupId'));
        const groups = groupedList.map((rows) => ({
            groupName: rows.at(0)?.groupName,
            rows: rows
                .filter((row) => row.value !== '')
                .map((row) => ({
                    label: row.label,
                    value: row.value,
                })),
        }));
        groups.forEach((group) => {
            if (group.rows.length === 0) return;
            const groupElement = (this.groupTemplateElement!.content.cloneNode(true) as HTMLElement).children[0];
            const groupTitleElement = groupElement.querySelector('[data-tk-title]');
            if (!groupTitleElement) return;
            groupTitleElement.textContent = group.groupName || '';

            group.rows.forEach((row) => {
                const rowElement = (this.rowTemplateElement!.content.cloneNode(true) as HTMLElement).children[0];
                const labelElement = rowElement.querySelector('[data-tk-label]');
                if (!labelElement) return;
                const valueElement = rowElement.querySelector('[data-tk-value]');
                if (!valueElement) return;
                labelElement.textContent = row.label;
                if (row.value.match(this.colorHexRegex)) {
                    valueElement.insertAdjacentElement(
                        'beforeend',
                        this.createColorElement(row.value),
                    );
                } else {
                    valueElement.textContent = row.value;
                }
                groupElement.insertAdjacentElement('beforeend', rowElement);
            });

            this.listWrapper?.insertAdjacentElement('beforeend', groupElement);
        });
        this.classList.add(this.loadedClassName);
        const showMoreComponent = this.closest<HTMLElement>('tk-show-more');
        showMoreComponent && showMoreComponent.dispatchEvent(new Event('tk-show-more-recalc'));
    }

    createColorElement(colorHexString: string) {
        const colorElement = document.createElement('div');
        colorElement.classList.add(this.colorClassName);
        colorElement.style.backgroundColor = colorHexString;
        colorElement.title = colorHexString;
        return colorElement;
    }
}
